import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Box, 
  Stack, 
  Text, 
  Button, 
  Progress, 
  Paper, 
  Group, 
  ActionIcon, 
  Badge,
  Checkbox,
  Title,
  Flex,
  List,
  Collapse,
  Menu
} from '@mantine/core';
import { 
  IconChevronLeft, 
  IconChevronRight, 
  IconClock,
  IconCheck,
  IconUsers,
  IconFlame,
  IconList,
  IconChevronDown,
  IconChevronUp,
  IconReplace,
  IconDotsVertical
} from '@tabler/icons-react';
import { Recipe, RecipeInstruction, Ingredient } from '../../types/mealPlan';
import { formatTime } from '../../utils/formatTime';
import { formatIngredientName, formatIngredientQuantity, formatIngredientPreparation } from '../../utils/formatIngredient';
import { MobileOptimized } from '../Layout/MobileOptimized';
import { useWakeLock } from '../../hooks/useWakeLock';
import { StepTimerText } from './StepTimerText';
import { makeTimerOrigin, pendingStepFocus, useStepFocus } from '../../contexts/CookingTimersContext';
import { useAuth } from '../../hooks/useAuth';
import { useAppUser } from '../../hooks/useAppUser';
import { SubstitutionDrawer } from './SubstitutionDrawer';
import { DrawerHeader } from '../UI/DrawerHeader';
import { ResponsiveDrawer, useIsBottomSheet } from '../UI/ResponsiveDrawer';
import classes from './CookView.module.css';
import { useMediaQuery } from '@mantine/hooks';

interface CookViewProps {
  recipe: Recipe;
  onBack: () => void;
  mealPlanId?: string;
  assignmentId?: string;
  isOwner?: boolean;
}

type ViewMode = 'ingredients' | 'cooking';

export const CookView = ({ recipe, onBack, mealPlanId, assignmentId, isOwner = true }: CookViewProps) => {
  // The all-ingredients drawer is a bottom sheet on mobile; its substitution
  // menu must not body-portal there
  const isBottomSheet = useIsBottomSheet();
  const { user } = useAuth();
  const { user: appUser } = useAppUser();
  const isAdmin = appUser?.isAdmin || !!appUser?._adminOverride;
  const location = useLocation();
  const stepFocusCtx = useStepFocus();
  // Arriving from a tray timer: open straight on its step. Seeded in the
  // initializers (not an effect) so the ingredients screen never flashes.
  const focusStep = pendingStepFocus(stepFocusCtx, recipe.id);
  const clampStep = (i: number) => Math.min(Math.max(0, i), Math.max(0, recipe.instructions.length - 1));
  const [viewMode, setViewMode] = useState<ViewMode>(() => (focusStep != null ? 'cooking' : 'ingredients'));
  const [currentStep, setCurrentStep] = useState(() => (focusStep != null ? clampStep(focusStep) : 0));
  const [checkedIngredients, setCheckedIngredients] = useState<Set<number>>(new Set());
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [stepIngredientsExpanded, setStepIngredientsExpanded] = useState(false);
  const { isSupported: wakeLockSupported, requestWakeLock, releaseWakeLock } = useWakeLock();
  
  // Substitution state
  const [substitutionDrawerOpened, setSubstitutionDrawerOpened] = useState(false);
  const [substitutionTarget, setSubstitutionTarget] = useState<Ingredient | null>(null);
  const [substitutionLoading, setSubstitutionLoading] = useState(false);
  const [substitutionError, setSubstitutionError] = useState<string | null>(null);

  // Mobile breakpoint
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Cook event tracking for preference learning
  const cookingStartTimeRef = useRef<number | null>(null);
  const maxStepReachedRef = useRef<number>(0);
  const cookEventFiredRef = useRef<boolean>(false);

  // Thresholds for firing cook event
  const COOK_EVENT_MIN_DURATION_SECONDS = 300; // 5 minutes
  const COOK_EVENT_MIN_STEPS = 2;

  // Function to fire cook event when threshold is met
  const fireCookEventIfThresholdMet = useCallback(async () => {
    // Skip if already fired, not logged in, or not the recipe owner
    if (cookEventFiredRef.current || !user || !isOwner || isAdmin) return;

    // Check if in cooking mode and start time is set
    if (!cookingStartTimeRef.current) return;

    const elapsedSeconds = Math.floor((Date.now() - cookingStartTimeRef.current) / 1000);
    const stepsCompleted = maxStepReachedRef.current + 1; // Convert 0-indexed to count

    // Check if both thresholds are met
    if (elapsedSeconds >= COOK_EVENT_MIN_DURATION_SECONDS && stepsCompleted >= COOK_EVENT_MIN_STEPS) {
      cookEventFiredRef.current = true;

      try {
        const token = await user.getIdToken();
        await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/cook-event`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            recipeId: recipe.id,
            mealPlanId,
            stepsCompleted,
            totalSteps: recipe.instructions.length,
          }),
        });
        console.log('[CookView] Cook event logged successfully');
      } catch (error) {
        console.error('[CookView] Failed to log cook event:', error);
        // Reset flag to allow retry
        cookEventFiredRef.current = false;
      }
    }
  }, [user, isOwner, isAdmin, recipe.id, recipe.instructions.length, mealPlanId]);

  // Handle back button - release wake lock when exiting cook mode
  const handleBack = useCallback(() => {
    if (wakeLockSupported) {
      releaseWakeLock();
    }
    onBack();
  }, [onBack, releaseWakeLock, wakeLockSupported]);

  // Gesture/touch handlers for navigation
  const handleNextStep = useCallback(() => {
    if (viewMode === 'ingredients') {
      setViewMode('cooking');
      setCurrentStep(0);
      // Scroll to top when starting cooking
      window.scrollTo(0, 0);
    } else if (currentStep < recipe.instructions.length - 1) {
      setCurrentStep(prev => prev + 1);
      // Scroll to top when moving to next step
      window.scrollTo(0, 0);
    } else {
      // On the last step, exit cook mode
      handleBack();
    }
  }, [viewMode, currentStep, recipe.instructions.length, handleBack]);

  const handlePrevStep = useCallback(() => {
    if (viewMode === 'cooking' && currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      // Scroll to top when moving to previous step
      window.scrollTo(0, 0);
    } else if (viewMode === 'cooking' && currentStep === 0) {
      setViewMode('ingredients');
      // Scroll to top when going back to ingredients
      window.scrollTo(0, 0);
    }
  }, [viewMode, currentStep]);

  // Consume the focus request; also handles a tray tap while already here.
  useEffect(() => {
    if (focusStep == null) return;
    setViewMode('cooking');
    setCurrentStep(clampStep(focusStep));
    window.scrollTo(0, 0);
    stepFocusCtx?.clearStepFocus();
    // clampStep/stepFocusCtx are stable enough; re-run only on a new request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusStep]);

  // Request wake lock when component mounts
  useEffect(() => {
    if (wakeLockSupported) {
      requestWakeLock();
    }
  }, [requestWakeLock, wakeLockSupported]);

  // Track when cooking mode starts
  useEffect(() => {
    if (viewMode === 'cooking' && !cookingStartTimeRef.current) {
      cookingStartTimeRef.current = Date.now();
    }
  }, [viewMode]);

  // Track max step reached and check threshold
  useEffect(() => {
    if (viewMode === 'cooking') {
      // Update max step reached
      if (currentStep > maxStepReachedRef.current) {
        maxStepReachedRef.current = currentStep;
      }
      // Check if threshold is met
      fireCookEventIfThresholdMet();
    }
  }, [viewMode, currentStep, fireCookEventIfThresholdMet]);

  // Periodically check threshold while in cooking mode (every 30 seconds)
  useEffect(() => {
    if (viewMode !== 'cooking') return;

    const intervalId = setInterval(() => {
      fireCookEventIfThresholdMet();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [viewMode, fireCookEventIfThresholdMet]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        handleNextStep();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevStep();
      } else if (e.key === 'Escape') {
        handleBack();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleNextStep, handlePrevStep, handleBack]);


  const toggleIngredient = (index: number) => {
    setCheckedIngredients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleGetSubstitutions = async (ingredient: Ingredient) => {
    setSubstitutionTarget(ingredient);
    setSubstitutionDrawerOpened(true);
    setSubstitutionLoading(true);
    setSubstitutionError(null);

    try {
      const token = await user?.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${recipe.id}/substitutions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          targetIngredient: ingredient,
          ...(mealPlanId && assignmentId && {
            mealPlanId,
            assignmentId
          })
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get substitutions');
      }

      const data = await response.json();
      
      // Update the ingredient with substitutions in the target
      setSubstitutionTarget(prev => prev ? { ...prev, substitutions: data.substitutions } : null);

    } catch (error) {
      console.error('Error getting substitutions:', error);
      setSubstitutionError(error instanceof Error ? error.message : 'Failed to get substitutions');
    } finally {
      setSubstitutionLoading(false);
    }
  };


  const totalSteps = recipe.instructions.length;
  const progress = viewMode === 'ingredients' ? 0 : ((currentStep + 1) / totalSteps) * 100;

  return (
    <MobileOptimized>
      <Stack gap="lg">
        {/* Header with Back Button */}
        <Box>
          <Group align="flex-start">
            <ActionIcon
              variant="subtle"
              onClick={handleBack}
              size="lg"
              style={{ marginTop: 4 }}
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <Stack gap={8} style={{ flex: 1 }}>
              <Title order={2} visibleFrom="sm">
                {recipe.name}
              </Title>
              <Title order={3} hiddenFrom="sm">
                {recipe.name}
              </Title>
            </Stack>
            <Group gap="sm" visibleFrom="sm">
              {viewMode === 'cooking' && (
                <ActionIcon
                  variant="light"
                  color="gray"
                  size="lg"
                  onClick={() => setDrawerOpened(true)}
                  title="View all ingredients"
                >
                  <IconList size={18} />
                </ActionIcon>
              )}
              <Badge
                variant="light"
                color={viewMode === 'ingredients' ? 'blue' : 'green'}
                size="lg"
              >
                Cook Mode
              </Badge>
            </Group>
          </Group>

          {/* Mobile controls */}
          <Group justify="center" hiddenFrom="sm" mt="sm">
            {viewMode === 'cooking' && (
              <ActionIcon
                variant="light"
                color="gray"
                size="lg"
                onClick={() => setDrawerOpened(true)}
                title="View all ingredients"
              >
                <IconList size={18} />
              </ActionIcon>
            )}
            <Badge
              variant="light"
              color={viewMode === 'ingredients' ? 'blue' : 'green'}
              size="lg"
            >
              Cook Mode
            </Badge>
          </Group>
        </Box>

        {/* Progress indicator for cooking steps */}
        {viewMode === 'cooking' && (
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Step {currentStep + 1} of {totalSteps}
              </Text>
              <Text size="sm" c="dimmed">
                {Math.round(progress)}% complete
              </Text>
            </Group>
            <Progress value={progress} size="md" radius="xl" />
          </Stack>
        )}


        {/* Recipe timing info */}
        <Flex gap="lg" wrap="wrap" justify="center">
          <Group gap="xs" align="flex-start">
            <IconClock size={20} style={{ marginTop: 2 }} />
            <Box>
              <Text size="sm" fw={500}>Prep time</Text>
              <Text size="sm" c="dimmed">{formatTime(recipe.prepTimeMinutes)}</Text>
            </Box>
          </Group>
          <Group gap="xs" align="flex-start">
            <IconFlame size={20} style={{ marginTop: 2 }} />
            <Box>
              <Text size="sm" fw={500}>Cook Time</Text>
              <Text size="sm" c="dimmed">{formatTime(recipe.cookTimeMinutes)}</Text>
            </Box>
          </Group>
          <Group gap="xs" align="flex-start">
            <IconUsers size={20} style={{ marginTop: 2 }} />
            <Box>
              <Text size="sm" fw={500}>Servings</Text>
              <Text size="sm" c="dimmed">{recipe.servings}</Text>
            </Box>
          </Group>
        </Flex>

        {/* Ingredients View */}
        {viewMode === 'ingredients' && (
          <Stack gap="md">
            <Title order={3}>Gather your ingredients</Title>
            
            <Stack gap="xs">
              {recipe.ingredients.map((ingredient, index) => (
                <Paper
                  key={index}
                  p="md"
                  radius="lg"
                  style={{
                    backgroundColor: checkedIngredients.has(index) ? 'var(--mantine-color-gray-0)' : 'var(--mantine-color-white)',
                    border: '1px solid var(--mantine-color-gray-2)',
                    // Checked already dims to 0.6; a diet conflict dims
                    // further, and the two take the lower value rather than
                    // stacking into invisibility.
                    opacity: ingredient.conflictsWith?.length
                      ? 0.45
                      : checkedIngredients.has(index)
                      ? 0.6
                      : 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => toggleIngredient(index)}
                  className={classes.ingredientRow}
                >
                  <Group align="flex-start" gap="md">
                    <Checkbox
                      checked={checkedIngredients.has(index)}
                      onChange={() => {}}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleIngredient(index);
                      }}
                      size="md"
                    />
                    <Box style={{ flex: 1 }}>
                      <Text 
                        size="md" 
                        fw={500}
                        style={{ 
                          textDecoration: checkedIngredients.has(index) ? 'line-through' : 'none',
                          lineHeight: 1.3
                        }}
                      >
                        <Text component="span" fw={700}>{formatIngredientQuantity(ingredient)}</Text> {formatIngredientName(ingredient)}
                      </Text>
                      {formatIngredientPreparation(ingredient) && (
                        <Text size="sm" c="dimmed" mt="xs">
                          {formatIngredientPreparation(ingredient)}
                        </Text>
                      )}
                      {ingredient.optional && (
                        <Badge size="sm" variant="light" color="gray" mt="xs">
                          Optional
                        </Badge>
                      )}
                      {ingredient.conflictsWith?.length ? (
                        <Badge size="sm" variant="light" color="orange" mt="xs" ml="xs">
                          not {ingredient.conflictsWith.join(', ').toLowerCase()}
                        </Badge>
                      ) : null}
                    </Box>
                    {isOwner && (
                      <Menu>
                        <Menu.Target>
                          <ActionIcon
                            size="md"
                            variant="subtle"
                            color="gray"
                            onClick={(e) => e.stopPropagation()}
                            className={classes.menuButton}
                          >
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconReplace size={isMobile ? 18 : 14} stroke={1.5} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGetSubstitutions(ingredient);
                            }}
                            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                          >
                            Find substitutes
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Stack>
        )}

        {/* Cooking Instructions View */}
        {viewMode === 'cooking' && (
          <Stack gap="md">
            <Title order={3}>Step {currentStep + 1}</Title>
            
            <Paper p="xl" radius="lg" withBorder>
              <Text
                size="lg" 
                lh={1.6}
                style={{ 
                  fontSize: '1.25rem',
                  lineHeight: '1.8'
                }}
              >
                {(() => {
                  const currentInstruction = recipe.instructions[currentStep];
                  const text = typeof currentInstruction === 'string'
                    ? currentInstruction
                    : currentInstruction.text;
                  return (
                    <StepTimerText
                      text={text}
                      timerKey={`${recipe.id}:${currentStep}`}
                      stepNumber={currentStep + 1}
                      recipeName={recipe.name}
                      origin={makeTimerOrigin(location, recipe.id, currentStep)}
                    />
                  );
                })()}
              </Text>
            </Paper>

            {/* Relevant Ingredients for this step */}
            {(() => {
              const currentInstruction = recipe.instructions[currentStep];
              
              // Check if this is the new format with ingredients
              if (typeof currentInstruction === 'object' && (currentInstruction as RecipeInstruction).ingredients && ((currentInstruction as RecipeInstruction).ingredients || []).length > 0) {
                // Find the actual ingredient objects from the recipe
                const relevantIngredients = ((currentInstruction as RecipeInstruction).ingredients || [])
                  .map(ingredientName => recipe.ingredients.find(ing => ing.name === ingredientName))
                  .filter(Boolean); // Remove any undefined matches
                
                if (relevantIngredients.length > 0) {
                  // Check which ingredients appear in multiple steps so we can hide
                  // the (total) quantity to avoid confusion
                  const multiStepIngredients = new Set<string>();
                  const ingredientStepCounts: Record<string, number> = {};
                  for (const inst of recipe.instructions) {
                    if (typeof inst === 'object' && inst.ingredients) {
                      for (const name of inst.ingredients) {
                        ingredientStepCounts[name] = (ingredientStepCounts[name] || 0) + 1;
                        if (ingredientStepCounts[name] > 1) {
                          multiStepIngredients.add(name);
                        }
                      }
                    }
                  }

                  return (
                    <Paper p="md" radius="lg" style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                      <Stack gap="xs">
                        <Group justify="space-between" style={{ cursor: 'pointer' }} onClick={() => setStepIngredientsExpanded(!stepIngredientsExpanded)}>
                          <Text size="sm" fw={500} c="blue.7">
                            Ingredients for this step ({relevantIngredients.length})
                          </Text>
                          <ActionIcon variant="subtle" size="sm" c="blue.7">
                            {stepIngredientsExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                          </ActionIcon>
                        </Group>
                        <Collapse in={stepIngredientsExpanded}>
                          <List size="sm" spacing="xs" c="blue.8">
                            {relevantIngredients.map((ingredient, index) => (
                              <List.Item key={index}>
                                <Text size="sm" style={{
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word'
                                }}>
                                  {multiStepIngredients.has(ingredient!.name)
                                    ? formatIngredientName(ingredient!)
                                    : <><Text component="span" fw={700}>{formatIngredientQuantity(ingredient!)}</Text> {formatIngredientName(ingredient!)}</>
                                  }
                                  {formatIngredientPreparation(ingredient!) && ` — ${formatIngredientPreparation(ingredient!)}`}
                                </Text>
                              </List.Item>
                            ))}
                          </List>
                        </Collapse>
                      </Stack>
                    </Paper>
                  );
                }
              }
              return null;
            })()}
          </Stack>
        )}

        {/* Navigation Controls */}
        <Group justify="space-between" align="center" mt="xl" wrap="nowrap" className="wizard-footer cook-nav">
          <Button
            variant="light"
            size="md"
            leftSection={<IconChevronLeft size={16} />}
            onClick={handlePrevStep}
            disabled={viewMode === 'ingredients'}
          >
            Previous
          </Button>

          <Button
            variant="filled"
            size="md"
            leftSection={
              viewMode === 'cooking' && currentStep === totalSteps - 1 ? 
                <IconCheck size={16} /> : 
                undefined
            }
            rightSection={
              viewMode === 'cooking' && currentStep === totalSteps - 1 ? 
                undefined :
                <IconChevronRight size={16} />
            }
            onClick={handleNextStep}
          >
            {viewMode === 'ingredients' 
              ? 'Start Cooking' 
              : currentStep === totalSteps - 1 
                ? 'Complete' 
                : 'Next'
            }
          </Button>
        </Group>

        {/* Bottom padding for mobile */}
        <Box h="2rem" />
      </Stack>

      {/* Full Ingredients Drawer */}
      <ResponsiveDrawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        title={
          <DrawerHeader
            icon={<IconList size={18} />}
            title="All ingredients"
          />
        }
        size="md"
      >
        <Stack gap="xs">
          {recipe.ingredients.map((ingredient, index) => (
            <Paper
              key={index}
              p="sm"
              radius="lg"
              style={{
                backgroundColor: checkedIngredients.has(index) ? 'var(--mantine-color-gray-0)' : 'var(--mantine-color-white)',
                border: '1px solid var(--mantine-color-gray-2)',
                opacity: ingredient.conflictsWith?.length
                  ? 0.45
                  : checkedIngredients.has(index)
                  ? 0.6
                  : 1,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => toggleIngredient(index)}
              className={classes.ingredientRow}
            >
              <Group align="flex-start" gap="sm">
                <Checkbox
                  checked={checkedIngredients.has(index)}
                  onChange={() => {}}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleIngredient(index);
                  }}
                  size="sm"
                />
                <Box style={{ flex: 1 }}>
                  <Text 
                    size="sm" 
                    fw={500}
                    style={{ 
                      textDecoration: checkedIngredients.has(index) ? 'line-through' : 'none',
                      lineHeight: 1.3
                    }}
                  >
                    <Text component="span" fw={700}>{formatIngredientQuantity(ingredient)}</Text> {formatIngredientName(ingredient)}
                    {formatIngredientPreparation(ingredient) && ` — ${formatIngredientPreparation(ingredient)}`}
                  </Text>
                  {ingredient.optional && (
                    <Badge size="xs" variant="light" color="gray" mt="xs">
                      Optional
                    </Badge>
                  )}
                  {ingredient.conflictsWith?.length ? (
                    <Badge size="xs" variant="light" color="orange" mt="xs" ml="xs">
                      not {ingredient.conflictsWith.join(', ').toLowerCase()}
                    </Badge>
                  ) : null}
                </Box>
                {isOwner && (
                  <Menu withinPortal={!isBottomSheet}>
                    <Menu.Target>
                      <ActionIcon
                        size="md"
                        variant="subtle"
                        color="gray"
                        onClick={(e) => e.stopPropagation()}
                        className={classes.menuButton}
                      >
                        <IconDotsVertical size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconReplace size={isMobile ? 18 : 14} stroke={1.5} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGetSubstitutions(ingredient);
                        }}
                        style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                      >
                        Find substitutes
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
            </Paper>
          ))}
        </Stack>
      </ResponsiveDrawer>

      {/* Substitution Drawer */}
      <SubstitutionDrawer
        opened={substitutionDrawerOpened}
        onClose={() => {
          setSubstitutionDrawerOpened(false);
          setSubstitutionTarget(null);
          setSubstitutionError(null);
        }}
        target={substitutionTarget}
        loading={substitutionLoading}
        error={substitutionError}
      />
    </MobileOptimized>
  );
};
